import { SERVICE_CITIES, type ServiceCity } from './cityMaterialContent';

// County-level landing pages. The site already ranks on city × material
// (/gravel-delivery/seattle/5-8-minus), but had nothing at the county level —
// which is exactly the query aggregator/marketplace sites target ("aggregate
// delivery near me → Washington → King County"). These are legitimate pages
// for us because we actually deliver countywide, and each one aggregates real
// cities, real zone fees, and real materials rather than being thin filler.
export interface ServiceCounty {
  slug: string;
  // Full display name, e.g. "King County".
  name: string;
  // Bare county name as stored on ServiceCity.county, e.g. "King".
  key: ServiceCity['county'];
  state: string;
  // One-line SEO/meta summary.
  blurb: string;
  // Longer on-page intro paragraph.
  intro: string;
  // Notable landmarks / sub-areas, used for local relevance copy.
  landmarks: string[];
  // Typical dispatch note for the county.
  leadTime: string;
}

export const SERVICE_COUNTIES: ServiceCounty[] = [{
  slug: 'king-county',
  name: 'King County',
  key: 'King',
  state: 'WA',
  blurb: 'Bulk gravel, crushed rock, topsoil, and bark delivered across King County — Seattle, Bellevue, Kent, Renton, Auburn, Federal Way, Redmond, and Sammamish.',
  intro: 'We haul aggregate across King County every day with our own trucks — no brokers, no marketplace middleman. Order crushed rock, drain rock, topsoil, or bark by the cubic yard, see your delivered price before you commit, and get it placed where you actually need it.',
  landmarks: ['Seattle city limits', 'the Eastside', 'the SeaTac and Kent Valley corridor', 'the Snoqualmie foothills'],
  leadTime: 'Same-day on most orders placed before noon'
}, {
  slug: 'pierce-county',
  name: 'Pierce County',
  key: 'Pierce',
  state: 'WA',
  blurb: 'Bulk gravel, crushed rock, topsoil, and bark delivered across Pierce County — Tacoma, Puyallup, Bonney Lake, and Gig Harbor.',
  intro: 'Pierce County is home turf. We run loads from Tacoma out through the Puyallup valley and across the Narrows every week, using our own dump trucks and drivers who know the roads, the grades, and the tight driveways.',
  landmarks: ['Tacoma and the Port', 'the Puyallup valley', 'the Bonney Lake plateau', 'the Gig Harbor peninsula'],
  leadTime: 'Same-day on most orders placed before noon'
}];

export function getServiceCounty(slug: string): ServiceCounty | undefined {
  return SERVICE_COUNTIES.find(c => c.slug === slug);
}

// Cities we publish landing pages for inside a county, ordered by delivery
// zone so the closest (cheapest to deliver to) read first.
export function getCitiesInCounty(county: ServiceCounty): ServiceCity[] {
  return SERVICE_CITIES.filter(c => c.county === county.key).slice().sort((a, b) => a.zone - b.zone || a.name.localeCompare(b.name));
}

export function getCountyForCity(city: ServiceCity): ServiceCounty | undefined {
  return SERVICE_COUNTIES.find(c => c.key === city.county);
}
